
/**
 * Interface for a similarity match result.
 */
export interface SimilarityMatch {
  existingClient: string;
  similarityScore: number;
  matchType: 'exact' | 'case' | 'spacing' | 'abbreviation';
}

/**
 * Service for detecting client name duplicates with similarity scoring.
 */
export class ClientSimilarityService {
  private static instance: ClientSimilarityService;

  /**
   * Get the singleton instance of the service.
   */
  public static getInstance(): ClientSimilarityService {
    if (!ClientSimilarityService.instance) {
      ClientSimilarityService.instance = new ClientSimilarityService();
    }
    return ClientSimilarityService.instance;
  }

  /**
   * Normalizes a client name for consistent comparison.
   * - Converts to lowercase
   * - Removes common business suffixes (e.g., "corp", "inc")
   * - Removes spaces and special characters
   *
   * @param name The client name to normalize.
   * @returns The normalized client name.
   */
  public normalizeClientName(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .toLowerCase()
      .replace(/\b(corp|corporation|inc|incorporated|llc|ltd)\b/g, '')
      .replace(/[\s-&,.]/g, '');
  }

  /**
   * Checks for duplicate client names using various similarity algorithms.
   *
   * @param inputName The new client name to check.
   * @param existingClients A list of existing client names.
   * @returns An array of similarity matches, sorted by score descending.
   */
  public checkForDuplicates(inputName: string, existingClients: string[]): SimilarityMatch[] {
    const normalizedInput = this.normalizeClientName(inputName);
    const matches: SimilarityMatch[] = [];

    if (!normalizedInput) {
      return [];
    }

    for (const existingClient of existingClients) {
      const normalizedExisting = this.normalizeClientName(existingClient);

      if (normalizedInput === normalizedExisting) {
        matches.push({
          existingClient,
          similarityScore: 100,
          matchType: 'exact',
        });
        continue; // Exact match is the highest score, no need to check further
      }

      if (inputName.toLowerCase() === existingClient.toLowerCase()) {
        matches.push({
          existingClient,
          similarityScore: 95,
          matchType: 'case',
        });
      }
      
      if (inputName.replace(/\s/g, '') === existingClient.replace(/\s/g, '')) {
        matches.push({
          existingClient,
          similarityScore: 90,
          matchType: 'spacing',
        });
      }
    }

    // Sort matches by score descending
    return matches.sort((a, b) => b.similarityScore - a.similarityScore);
  }
}

export default ClientSimilarityService.getInstance();
